/**
 * FindingFocusModal — full-screen focus view for a single finding.
 *
 * Opened from the "Explore deeper" action in the FindingDetailView header.
 * Background is dimmed (rgba(12,25,41,0.45)) + blurred; Esc, X button, or
 * a backdrop click all close the modal. Content is split into two columns:
 *   - Primary  (left, ~62%): the embedded article (EUR-Lex iframe / PDF /
 *     Q&A / concept definition text), reusing the workflow-orchestrator
 *     endpoint FindingDetailView already relies on.
 *   - Secondary (right, ~38%): a scannable rail of Agent's references,
 *     cross-references, linked Q&A, related concepts, and lineage
 *     metadata — everything an expert needs to triangulate.
 *
 * This view is additive: the inline master/detail panel is untouched and
 * behaves exactly as before. The only change in FindingDetailView is a new
 * button in the header that opens this modal.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  ExternalLink,
  Globe,
  ArrowUpRight,
  ArrowDownLeft,
  MessageSquare,
  BookOpen,
  Loader2,
  Hash,
  AlertTriangle,
} from 'lucide-react';
import {
  formatContent,
  formatQaContent,
  lightTheme,
  cleanSourceText,
  resolveAgentReferenceText,
} from '../../utils/contentFormatter';
import type { DictMarker } from '../../types/workspaceState';
import type { CitationContext } from './UnifiedFindingsPanel';
import ReferencePreview, { type PreviewRef } from './ReferencePreview';
import DialogShell from '../ui/DialogShell';
import { compactList, formatQaDate } from './qaUtils';
import {
  detectFindingSourceType,
  getSafeSourceUrl,
  isMeaningful,
  type SourceType,
} from '../../utils/findingSourceType';
import {
  buildEurLexUrl,
  buildEurLexViewerUrl,
  buildPdfViewerUrl,
  EURLEX_PREVIEW_SANDBOX,
  extractPdfDocId,
  extractPdfDocIdFromFields,
  findingAsOf,
  findPeerDocumentUrl,
  parsePageNumber,
} from '../../utils/findingSourceLinks';

// =============================================================================
// TYPES (kept in sync with FindingDetailView; local copy avoids cyclic imports)
// =============================================================================

interface Finding {
  finding_id: string;
  source_document_celex?: string;
  source_document_name?: string;
  source_reference_hierarchy?: string;
  source_reference?: string;
  source_text?: string;
  source_snippet?: string;
  extracted_at?: string;
  tool_used?: string;
  document_version?: string;
  valid_from?: string;       // BAR-533 version pin
  valid_to?: string;
  version_status?: string;
  source_service?: string;
  source_url?: string;
  source_type?: string;
  document_type?: string;
  document_full_title?: string;
  article_title?: string;
  legal_status?: string;
  chunk_id?: string;
  page_number?: number;
  paragraph_id?: string;
  bbox?: Record<string, any>;
  authority_abbrev?: string;
  authority_name_en?: string;
  lifecycle_stage?: string;
  reference_type?: string;
  pdf_url?: string;
  page_url?: string;
  publication_date?: string;
  regulatory_framework?: string[];
  legal_basis?: string[];
  applies_to?: string[];
  effective_date?: string;
  parent_reference?: string;
  replaces?: string;
  amends?: string[];
  compliance_deadline?: string;
  cross_references?: Array<{ direction: string; article: string; celex: string; document_name: string }>;
  concepts?: string[];
  linked_qa?: Array<{ ref: string; target: string; date?: string; title?: string }>;
  qa_topic?: string;
  qa_legal_act?: string;
  qa_status?: string;
  qa_submitted_date?: string;
  qa_clarifies?: string[];
  definition_sources?: Array<{ ref: string; celex: string; short_name: string }>;
  concept_relationships?: Record<string, string[]>;
}

const PDF_IFRAME_SPINNER_TIMEOUT_MS = 3000;
const EURLEX_IFRAME_FAILURE_TIMEOUT_MS = 45000;

interface FindingFocusModalProps {
  isOpen: boolean;
  finding: Finding;
  citationContext?: CitationContext | null;
  allFindings: Record<string, Finding>;
  allMarkers?: Record<string, DictMarker>;
  workspaceId?: string;
  compartmentId?: string;
  onClose: () => void;
  onFindingClick: (findingId: string) => void;
}

// =============================================================================
// HELPERS
// =============================================================================

function cleanArticleName(article: string): string {
  return article
    .replace(/\s*\(Consolidated TEXT:.*?\)/gi, '')
    .replace(/\s*\(Consolidated\s.*?\)/gi, '')
    .replace(/^Art\.\s+/, 'Article ')
    .trim();
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function stripLeadingReference(title: string, reference: string | undefined | null): string {
  if (!title || !reference) return title;
  return title
    .replace(new RegExp(`^\\s*${escapeRegExp(reference)}\\s*[-–—:]?\\s*`, 'i'), '')
    .trim();
}

function sourceTypeAccent(type: SourceType): string {
  switch (type) {
    case 'eurlex': return 'var(--bark-teal)';
    case 'pdf': return 'var(--bark-cyan)';
    case 'qa': return 'var(--bark-orange)';
    case 'concept': return 'var(--bark-purple)';
    default: return 'var(--fg-tertiary)';
  }
}

function deriveKicker(finding: Finding, sourceType: SourceType): string {
  if (sourceType === 'eurlex') {
    if (finding.legal_status === 'interpretive') return 'EU Regulation · Interpretive';
    if (finding.legal_status === 'comply_or_explain') return 'EU Regulation · Comply or explain';
    return 'EU Regulation · Binding';
  }
  if (sourceType === 'pdf') {
    const auth = finding.authority_abbrev || finding.authority_name_en || 'Guidance';
    const bindness = finding.legal_status === 'binding' ? 'Binding' : 'Non-binding';
    return `${auth} · ${bindness}`;
  }
  if (sourceType === 'qa') return 'Q&A · Provider-issued answer';
  if (sourceType === 'concept') return 'Concept · Regulatory definition';
  if (sourceType === 'user_upload') return 'Uploaded document';
  return 'Source';
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

/**
 * RailSection — labelled grouping on the right rail.
 * Count is optional; body is whatever children are passed.
 */
function RailSection({
  title, count, children,
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <section className="border-b border-divider pb-4 mb-4 last:border-0 last:mb-0 last:pb-0">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-tertiary">
          {title}
        </h3>
        {typeof count === 'number' && count > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-bg-warm text-fg-tertiary font-mono">
            {count}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

function QaMetadataHeader({ finding }: { finding: Finding }) {
  const authority = finding.authority_abbrev || finding.authority_name_en || 'EBA';
  const outgoing = finding.cross_references?.filter(x => x.direction === 'outgoing') || [];
  const appliesTo = isMeaningful(finding.qa_legal_act)
    ? [finding.qa_legal_act]
    : compactList([
        ...(finding.qa_clarifies || []),
        ...outgoing.map((xref) => cleanArticleName(xref.article)),
      ]);
  const rows = [
    { label: 'Question ID', value: finding.source_reference, mono: true },
    { label: 'Status', value: finding.qa_status },
    { label: 'Prepared by', value: authority },
    { label: 'Submitted', value: formatQaDate(finding.qa_submitted_date) },
    { label: 'Final answer published', value: formatQaDate(finding.publication_date) },
    { label: 'Topic', value: finding.qa_topic },
    { label: 'Applies to', value: appliesTo.length ? appliesTo.join(', ') : undefined },
  ].filter((row) => isMeaningful(row.value));

  if (!rows.length) return null;

  return (
    <div className="mb-7 pb-5 border-b border-divider">
      <dl className="grid grid-cols-[150px_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-[13px] leading-[1.45]">
        {rows.map((row) => (
          <React.Fragment key={row.label}>
            <dt className="text-fg-tertiary font-medium">{row.label}</dt>
            <dd className={`text-fg-secondary min-w-0 break-words ${row.mono ? 'font-mono text-[12.5px]' : ''}`}>
              {row.value}
            </dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
}

/**
 * PrimaryView — renders the full article. Reuses the exact same endpoints
 * as FindingDetailView (eurlex article viewer, pdf proxy, q&a text, concept
 * definitions), but sized up for focused reading.
 */
function PrimaryView({
  finding,
  sourceType,
  eurLexUrl,
  highlightText,
}: {
  finding: Finding;
  sourceType: SourceType;
  eurLexUrl: string | null;
  highlightText?: string;
}) {
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const viewerUrl = useMemo(() => {
    if (sourceType !== 'eurlex') return null;
    if (!finding.source_document_celex || !finding.source_reference) return null;
    const base = buildEurLexViewerUrl(
      finding.source_document_celex, finding.source_reference, findingAsOf(finding),
    );
    if (!highlightText) return base;
    const truncated = highlightText.substring(0, 200);
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}highlight=${encodeURIComponent(truncated)}`;
  }, [finding.source_document_celex, finding.source_reference, finding.valid_from, finding.valid_to, sourceType, highlightText]);

  const pdfUrl = useMemo(() => {
    if (sourceType !== 'pdf') return null;
    const docId = extractPdfDocId(finding);
    if (!docId) return null;
    const pageNum = finding.page_number ?? parsePageNumber(finding.source_reference);
    return buildPdfViewerUrl(docId, pageNum);
  }, [finding, sourceType]);

  const hasIframe = (sourceType === 'eurlex' && viewerUrl) || (sourceType === 'pdf' && pdfUrl);
  const iframeSrc = sourceType === 'eurlex' ? viewerUrl : pdfUrl;

  useEffect(() => {
    setIframeLoading(true);
    setIframeError(false);
  }, [iframeSrc]);

  useEffect(() => {
    if (!hasIframe || !iframeLoading) return;
    const timer = setTimeout(() => {
      if (sourceType === 'pdf') {
        setIframeLoading(false);
      } else {
        setIframeLoading(false);
        setIframeError(true);
      }
    }, sourceType === 'pdf' ? PDF_IFRAME_SPINNER_TIMEOUT_MS : EURLEX_IFRAME_FAILURE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [iframeSrc, retryCount, hasIframe, iframeLoading, sourceType]);

  // Concept finding — render the definition + first source when available
  if (sourceType === 'concept') {
    const definition = finding.source_snippet || finding.source_text || '';
    const sources = finding.definition_sources || [];
    const firstSource = sources[0];
    const conceptIframe = firstSource
      ? buildEurLexViewerUrl(firstSource.celex, firstSource.ref)
      : null;
    return (
      <div className="h-full flex flex-col overflow-hidden">
        {definition && (
          <div className="flex-shrink-0 p-6 bg-[color:var(--bark-purple-light)] border-b border-border-default">
            <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-[0.08em] mb-2">
              Definition
            </div>
            <p className="font-body text-[15px] leading-[1.65] text-fg">
              {cleanSourceText(definition)}
            </p>
          </div>
        )}
        {conceptIframe ? (
          <iframe
            src={conceptIframe}
            className="flex-1 w-full border-0"
            title={`Source: ${firstSource?.ref}`}
            sandbox={EURLEX_PREVIEW_SANDBOX}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-fg-tertiary p-8 text-center">
            This concept has no linked source document to display.
          </div>
        )}
      </div>
    );
  }

  // Iframe-based (EUR-Lex / PDF)
  if (hasIframe && iframeSrc) {
    return (
      <div className="h-full relative bg-surface">
        {iframeLoading && !iframeError && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface z-10">
            <div
              className="bg-surface border border-border-default rounded-lg shadow-bark-md px-6 py-5 max-w-[340px]"
              role="status"
              aria-live="polite"
            >
              <div className="flex items-center gap-3 mb-3">
                <Loader2 size={20} className="animate-spin" style={{ color: 'var(--accent)' }} />
                <span className="text-sm font-semibold text-fg">
                  {sourceType === 'pdf' ? 'Loading PDF' : 'Loading article'}
                </span>
              </div>
              <div className="relative h-1.5 bg-bg-warm rounded overflow-hidden mb-2">
                <div
                  className="absolute inset-y-0 w-1/3 rounded bark-bar-slide"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent 0%, var(--accent) 40%, var(--accent) 60%, transparent 100%)',
                  }}
                />
              </div>
              <div className="text-[11px] text-fg-tertiary">
                Fetching from the authoritative source. Typically 3&ndash;5 seconds.
              </div>
            </div>
          </div>
        )}
        {iframeError && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface z-10">
            <div className="text-center space-y-3 max-w-sm px-6">
              <AlertTriangle size={24} className="text-warning mx-auto" />
              <p className="text-sm text-fg-secondary">Unable to load source content.</p>
              <div className="flex items-center gap-3 justify-center">
                <button
                  onClick={() => { setRetryCount(c => c + 1); setIframeLoading(true); setIframeError(false); }}
                  className="px-3 py-1.5 text-xs bg-accent text-white rounded hover:bg-accent-hover font-medium"
                >
                  Retry
                </button>
                {eurLexUrl && (
                  <a
                    href={eurLexUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 text-xs border border-border-default text-fg-secondary rounded hover:bg-bg-warm font-medium"
                  >
                    Open on EUR-Lex
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
        <iframe
          key={retryCount}
          src={iframeSrc}
          className="w-full h-full border-0"
          title={`Source: ${finding.source_reference}`}
          {...(sourceType === 'pdf'
            ? {}
            : { sandbox: EURLEX_PREVIEW_SANDBOX }
          )}
          onLoad={() => { setIframeLoading(false); setIframeError(false); }}
          onError={() => { setIframeLoading(false); setIframeError(true); }}
        />
      </div>
    );
  }

  // Text-based source content (Q&A, uploaded document, unknown)
  return (
    <div className="h-full overflow-y-auto p-8 bg-surface">
      <div className="max-w-[720px] mx-auto space-y-4">
        {sourceType === 'qa' && <QaMetadataHeader finding={finding} />}
        {finding.source_text ? (
          <div className="font-body text-[15px] leading-[1.7] text-fg">
            {sourceType === 'qa'
              ? formatQaContent(cleanSourceText(finding.source_text), lightTheme)
              : formatContent(cleanSourceText(finding.source_text), lightTheme)}
          </div>
        ) : (
          <div className="text-center py-16 text-sm text-fg-tertiary">
            No source content available for this finding.
          </div>
        )}
      </div>
    </div>
  );
}

function FocusLoadingState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center bg-surface-sunken">
      <Loader2 size={22} className="animate-spin text-fg-tertiary" />
      <span className="ml-2 text-sm text-fg-tertiary">{label}</span>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function FindingFocusModal({
  isOpen,
  finding,
  citationContext,
  allFindings,
  allMarkers,
  workspaceId,
  compartmentId,
  onClose,
  onFindingClick,
}: FindingFocusModalProps) {
  const titleId = React.useId();
  const pendingNavigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [previewRef, setPreviewRef] = useState<PreviewRef | null>(null);
  const [pendingFindingId, setPendingFindingId] = useState<string | null>(null);

  const sourceType = detectFindingSourceType(finding);
  const eurLexUrl = buildEurLexUrl(finding.source_document_celex, {
    articleRef: finding.source_reference,
    asOf: findingAsOf(finding),
  });
  const accent = sourceTypeAccent(sourceType);
  const kicker = deriveKicker(finding, sourceType);

  // Derived title — Inter body face. Ref rendered separately.
  const ref = finding.source_reference && sourceType !== 'concept' ? finding.source_reference : null;
  const displayTitle = useMemo(() => {
    const rawTitle =
      finding.article_title ||
      (sourceType === 'concept' ? finding.source_document_name : undefined) ||
      finding.source_reference ||
      finding.source_document_name ||
      finding.finding_id;
    return stripLeadingReference(rawTitle, ref);
  }, [
    finding.article_title,
    finding.finding_id,
    finding.source_document_name,
    finding.source_reference,
    ref,
    sourceType,
  ]);

  // Breadcrumb
  const breadcrumbs = useMemo(() => {
    const crumbs: string[] = [];
    if (finding.source_document_name) crumbs.push(finding.source_document_name);
    if (finding.source_reference_hierarchy) {
      const parts = finding.source_reference_hierarchy
        .split(/[>/]/).map(s => s.trim().replace(/\s*\(.*?\)\s*$/, ''))
        .filter(Boolean);
      crumbs.push(...parts.slice(-2));
    }
    return crumbs;
  }, [finding.source_document_name, finding.source_reference_hierarchy]);

  const peerDocumentUrl = useMemo(() => {
    if (sourceType !== 'pdf') return null;
    return findPeerDocumentUrl(
      finding.finding_id,
      finding.source_document_celex,
      finding.source_document_name,
      allFindings,
    );
  }, [
    sourceType,
    finding.finding_id,
    finding.source_document_celex,
    finding.source_document_name,
    allFindings,
  ]);

  // Action link (open source — always visible when available)
  const actionLink = useMemo(() => {
    const safeSourceUrl = getSafeSourceUrl(finding.source_url);
    const safePdfUrl = getSafeSourceUrl(finding.pdf_url);
    const safePageUrl = getSafeSourceUrl(finding.page_url);

    if (sourceType === 'eurlex' && eurLexUrl) return { url: eurLexUrl, label: 'Open on EUR-Lex' };
    if (sourceType === 'qa' && safeSourceUrl) return { url: safeSourceUrl, label: 'Open source' };
    if (sourceType === 'pdf') {
      const url = safeSourceUrl || safePdfUrl || safePageUrl || peerDocumentUrl;
      if (url) return { url, label: 'Open PDF source' };
      const docId = extractPdfDocIdFromFields(finding.chunk_id, finding.source_document_celex);
      if (docId) {
        const proxyUrl = buildPdfViewerUrl(docId, null);
        if (proxyUrl) return { url: proxyUrl, label: 'Open PDF' };
      }
    }
    if (safeSourceUrl) return { url: safeSourceUrl, label: 'Open source' };
    return null;
  }, [
    sourceType,
    eurLexUrl,
    finding.source_url,
    finding.pdf_url,
    finding.page_url,
    finding.chunk_id,
    finding.source_document_celex,
    peerDocumentUrl,
  ]);

  // Markers referencing this finding
  const findingMarkers = useMemo(() => {
    if (!allMarkers) return [];
    return Object.values(allMarkers).filter(m => m.finding_id === finding.finding_id);
  }, [allMarkers, finding.finding_id]);

  // Highlight text for EUR-Lex deep-link
  const highlightText = useMemo(() => {
    if (sourceType !== 'eurlex') return undefined;
    if (finding.source_snippet) {
      const cleaned = cleanSourceText(finding.source_snippet);
      return cleaned || undefined;
    }
    return undefined;
  }, [finding.source_snippet, sourceType]);

  // Fallback Agent's Reference when no structured markers exist — the
  // agent's quote in full, never character-truncated (the rail scrolls).
  const fallbackReference = useMemo(() => {
    if (findingMarkers.length > 0) return null;
    if (citationContext?.citation_text) return citationContext.citation_text;
    if (sourceType === 'concept') return null;
    const raw = resolveAgentReferenceText(finding.source_text, finding.source_snippet);
    return raw ? cleanSourceText(raw) || null : null;
  }, [findingMarkers.length, citationContext, finding.source_text, finding.source_snippet, sourceType]);

  // Cross-references
  const outgoing = finding.cross_references?.filter(x => x.direction === 'outgoing') || [];
  const incoming = finding.cross_references?.filter(x => x.direction !== 'outgoing') || [];
  const allRefs = [...outgoing, ...incoming];

  const clearPendingNavigationTimeout = useCallback(() => {
    if (pendingNavigationTimeoutRef.current) {
      clearTimeout(pendingNavigationTimeoutRef.current);
      pendingNavigationTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    clearPendingNavigationTimeout();
    setPreviewRef(null);
    setPendingFindingId(null);
  }, [finding.finding_id, isOpen, clearPendingNavigationTimeout]);

  useEffect(() => {
    return clearPendingNavigationTimeout;
  }, [clearPendingNavigationTimeout]);

  const handleFindingNavigation = useCallback((findingId: string) => {
    clearPendingNavigationTimeout();
    setPreviewRef(null);
    if (findingId !== finding.finding_id) {
      setPendingFindingId(findingId);
      pendingNavigationTimeoutRef.current = setTimeout(() => {
        setPendingFindingId(current => current === findingId ? null : current);
        pendingNavigationTimeoutRef.current = null;
      }, 10000);
    } else {
      setPendingFindingId(null);
    }
    onFindingClick(findingId);
  }, [finding.finding_id, onFindingClick, clearPendingNavigationTimeout]);

  const findFindingForArticle = useCallback((article: string, celex: string): string | null => {
    if (!celex) return null;
    const cleaned = cleanArticleName(article);
    for (const f of Object.values(allFindings)) {
      if (f.source_document_celex !== celex) continue;
      if (f.source_reference === article || f.source_reference === cleaned) return f.finding_id;
      if (f.article_title === article || f.article_title === cleaned) return f.finding_id;
    }
    return null;
  }, [allFindings]);

  const findFindingForQa = useCallback((qaRef: string): string | null => {
    for (const f of Object.values(allFindings)) {
      if (f.source_reference === qaRef) return f.finding_id;
    }
    return null;
  }, [allFindings]);

  const findConceptFinding = useCallback((conceptName: string): Finding | null => {
    for (const f of Object.values(allFindings)) {
      if (f.source_type === 'concept' && (
        f.article_title === conceptName ||
        f.source_document_name === conceptName ||
        f.source_reference === conceptName
      )) return f;
    }
    return null;
  }, [allFindings]);

  // Lineage items (publication dates, legal-status, lineage refs, framework pills)
  const hasLineage = !!(
    finding.publication_date ||
    finding.effective_date ||
    finding.compliance_deadline ||
    finding.parent_reference ||
    finding.replaces ||
    (finding.amends && finding.amends.length > 0) ||
    (finding.regulatory_framework && finding.regulatory_framework.length > 0) ||
    (finding.applies_to && finding.applies_to.length > 0) ||
    finding.legal_status ||
    finding.document_version
  );

  if (!isOpen) return null;

  return (
    <DialogShell
      open={isOpen}
      onClose={onClose}
      labelledBy={titleId}
      backdropPolicy="dismiss"
      overlayClassName="flex items-center justify-center p-4 md:p-8 font-body"
      panelClassName="relative w-full h-full max-w-[1400px] max-h-[92vh] border border-border-default flex flex-col overflow-hidden"
    >
        {/* Accent bar */}
        <div
          className="absolute left-0 top-0 bottom-0 w-1"
          style={{ background: accent }}
          aria-hidden
        />

        {/* Header — kicker, title, breadcrumb, actions, close */}
        <header className="flex-shrink-0 pl-5 pr-4 py-4 border-b border-border-default">
          <div className="flex items-start gap-4">
            <div className="flex-1 min-w-0">
              {/* Kicker — source-type label */}
              <div className="flex items-center gap-1.5 mb-1.5">
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: accent }}
                  aria-hidden
                />
                <span
                  className="text-[10px] font-semibold uppercase tracking-[0.08em]"
                  style={{ color: accent }}
                >
                  {kicker}
                </span>
              </div>

              {/* Title — source reference + title as one heading */}
              <h2
                id={titleId}
                className="font-body text-[22px] md:text-[26px] font-semibold leading-[1.2] tracking-normal text-fg break-words"
              >
                {ref && (
                  <span className="mr-2">
                    {ref}
                  </span>
                )}
                {displayTitle}
              </h2>

              {/* Breadcrumb trail */}
              {breadcrumbs.length > 0 && (
                <div className="flex items-center gap-1.5 text-[12px] text-fg-tertiary mt-2 flex-wrap">
                  {breadcrumbs.map((c, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <span className="text-fg-tertiary/40">/</span>}
                      <span
                        className={
                          (i === breadcrumbs.length - 1 ? 'text-fg-secondary font-medium ' : '') +
                          'truncate max-w-[220px]'
                        }
                        title={c}
                      >
                        {c}
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>

            {/* Actions — Open source (always visible), Close */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {actionLink && (
                <a
                  href={actionLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent text-white hover:bg-accent-hover rounded text-[12px] font-semibold transition-colors"
                  title={actionLink.label}
                >
                  <Globe size={13} />
                  <span className="hidden sm:inline">{actionLink.label}</span>
                  <ExternalLink size={11} className="opacity-80" />
                </a>
              )}
              <button
                onClick={onClose}
                className="p-2 rounded hover:bg-bg-warm transition-colors text-fg-tertiary hover:text-fg"
                aria-label="Close focus view"
                title="Close (Esc)"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </header>

        {/* Body — primary (left) + rail (right) */}
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Primary — full article */}
          <div className="flex-1 min-w-0 border-r border-border-default bg-surface-sunken">
            {previewRef ? (
              <ReferencePreview
                previewRef={previewRef}
                workspaceId={workspaceId || ''}
                compartmentId={compartmentId || 'default'}
                onClose={() => setPreviewRef(null)}
                onFindingCreated={handleFindingNavigation}
              />
            ) : pendingFindingId ? (
              <FocusLoadingState label="Loading finding..." />
            ) : (
              <PrimaryView
                finding={finding}
                sourceType={sourceType}
                eurLexUrl={eurLexUrl}
                highlightText={highlightText}
              />
            )}
          </div>

          {/* Secondary rail — cross-refs, Q&A, concepts, lineage */}
          <aside className="w-[340px] lg:w-[380px] flex-shrink-0 overflow-y-auto bg-surface p-5">
            {/* Why Cited */}
            {citationContext?.context && (
              <RailSection title="Why cited">
                <div className="p-3 rounded bg-accent-tint border-l-4 border-accent">
                  <p className="text-[13px] leading-[1.55] text-fg">{citationContext.context}</p>
                </div>
              </RailSection>
            )}

            {/* Agent's references */}
            {(findingMarkers.length > 0 || fallbackReference) && (
              <RailSection
                title={findingMarkers.length > 1 ? "Agent's references" : "Agent's reference"}
                count={findingMarkers.length || undefined}
              >
                {findingMarkers.length > 0 ? (
                  <ul className="space-y-2">
                    {findingMarkers.map((marker) => {
                      const isActive = citationContext?.label === marker.label;
                      return (
                        <li
                          key={marker.marker_id}
                          className={`text-[12.5px] p-2.5 rounded leading-[1.55] ${
                            isActive
                              ? 'bg-accent-tint ring-1 ring-accent'
                              : 'bg-accent-tint/60'
                          }`}
                          style={{ borderLeft: '3px solid var(--accent)' }}
                        >
                          <div className="flex items-baseline gap-1.5">
                            <span className="font-mono font-semibold text-accent-hover">{marker.label}</span>
                            {marker.citation_text && (
                              <span className="text-fg whitespace-pre-line">{marker.citation_text}</span>
                            )}
                          </div>
                          {marker.context && (
                            <p className="text-fg-tertiary mt-1 whitespace-pre-line">{marker.context}</p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p
                    className="text-[12.5px] p-3 rounded bg-accent-tint/60 leading-[1.6] text-fg whitespace-pre-line"
                    style={{ borderLeft: '3px solid var(--accent)' }}
                  >
                    {fallbackReference}
                  </p>
                )}
              </RailSection>
            )}

            {/* Cross-references */}
            {allRefs.length > 0 && (
              <RailSection title="Cross-references" count={allRefs.length}>
                <ul className="space-y-1.5">
                  {allRefs.map((xref, i) => {
                    const findingId = findFindingForArticle(xref.article, xref.celex);
                    const isOutgoing = xref.direction === 'outgoing';
                    const label = cleanArticleName(xref.article);
                    return (
                      <li key={i} className="flex items-start gap-1.5 text-[12.5px]">
                        {isOutgoing ? (
                          <ArrowUpRight size={12} className="text-link flex-shrink-0 mt-0.5" aria-label="outgoing" />
                        ) : (
                          <ArrowDownLeft size={12} className="text-success flex-shrink-0 mt-0.5" aria-label="incoming" />
                        )}
                        <div className="min-w-0 flex-1">
                          {findingId ? (
                            <button
                              onClick={() => handleFindingNavigation(findingId)}
                              className="text-accent hover:text-accent-hover hover:underline text-left font-mono font-medium truncate max-w-full block"
                            >
                              {label}
                            </button>
                          ) : xref.celex ? (
                            <button
                              onClick={() => setPreviewRef({
                                celex: xref.celex,
                                articleRef: label,
                                documentName: xref.document_name || xref.celex,
                                type: xref.celex === 'EBA_Single_Rulebook_QA' ? 'qa' : 'eurlex',
                              })}
                              className="text-accent hover:text-accent-hover hover:underline text-left font-mono font-medium truncate max-w-full block"
                              title={`Preview ${label}`}
                            >
                              {label}
                            </button>
                          ) : (
                            <span className="text-fg-secondary font-mono truncate block">{label}</span>
                          )}
                          {xref.document_name && (
                            <span className="block text-[11px] text-fg-tertiary truncate">
                              {xref.document_name}
                            </span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </RailSection>
            )}

            {/* Linked Q&A */}
            {finding.linked_qa && finding.linked_qa.length > 0 && (
              <RailSection title="Linked Q&A" count={finding.linked_qa.length}>
                <ul className="space-y-1.5">
                  {finding.linked_qa.map((qa, i) => {
                    const qaRef = qa.ref?.trim() || '';
                    const findingId = qaRef ? findFindingForQa(qaRef) : null;
                    const qaLabel = qa.title || qaRef || 'Q&A reference';
                    return (
                      <li key={i} className="flex items-start gap-1.5 text-[12.5px]">
                        <MessageSquare size={12} className="text-warning flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          {findingId ? (
                            <button
                              onClick={() => handleFindingNavigation(findingId)}
                              className="text-accent hover:text-accent-hover hover:underline text-left block"
                            >
                              {qaLabel}
                            </button>
                          ) : qaRef ? (
                            <button
                              onClick={() => setPreviewRef({
                                celex: 'EBA_Single_Rulebook_QA',
                                articleRef: qaRef,
                                documentName: 'EBA Q&A',
                                type: 'qa',
                              })}
                              className="text-accent hover:text-accent-hover hover:underline text-left block"
                              title={`Preview Q&A ${qaRef}`}
                            >
                              {qaLabel}
                            </button>
                          ) : (
                            <span className="text-fg-secondary block">{qaLabel}</span>
                          )}
                          {qa.title && qaRef && qaRef !== qa.title && (
                            <span className="block text-[11px] text-fg-tertiary font-mono truncate">
                              {qaRef}
                            </span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </RailSection>
            )}

            {/* Related concepts */}
            {finding.concepts && finding.concepts.length > 0 && (
              <RailSection title="Related concepts" count={finding.concepts.length}>
                <div className="flex flex-wrap gap-1.5">
                  {finding.concepts.map((concept, i) => {
                    const conceptFinding = findConceptFinding(concept);
                    return conceptFinding ? (
                      <button
                        key={i}
                        onClick={() => handleFindingNavigation(conceptFinding.finding_id)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[12px] bg-[color:var(--bark-purple-light)] text-node-definition hover:ring-1 hover:ring-node-definition transition-all"
                        title={`View concept: ${concept}`}
                      >
                        <BookOpen size={10} />
                        {concept}
                      </button>
                    ) : (
                      <span
                        key={i}
                        className="inline-block px-2 py-1 rounded text-[12px] bg-bg-warm text-fg-secondary"
                      >
                        {concept}
                      </span>
                    );
                  })}
                </div>
              </RailSection>
            )}

            {/* Lineage / regulatory metadata */}
            {hasLineage && (
              <RailSection title="Lineage">
                <dl className="space-y-1.5 text-[12.5px]">
                  {finding.publication_date && (
                    <LineageItem label="Published" value={finding.publication_date} />
                  )}
                  {finding.effective_date && (
                    <LineageItem label="Effective" value={finding.effective_date} />
                  )}
                  {finding.compliance_deadline && (
                    <LineageItem label="Compliance" value={finding.compliance_deadline} />
                  )}
                  {finding.legal_status && (
                    <LineageItem
                      label="Legal status"
                      value={finding.legal_status.replace(/_/g, ' ')}
                    />
                  )}
                  {finding.parent_reference && (
                    <LineageItem label="Parent" value={finding.parent_reference} mono />
                  )}
                  {finding.replaces && (
                    <LineageItem label="Replaces" value={finding.replaces} mono />
                  )}
                  {finding.amends && finding.amends.length > 0 && (
                    <LineageItem label="Amends" value={finding.amends.join(', ')} mono />
                  )}
                  {finding.regulatory_framework && finding.regulatory_framework.length > 0 && (
                    <div className="pt-1">
                      <dt className="text-fg-tertiary mb-1">Framework</dt>
                      <dd className="flex flex-wrap gap-1">
                        {finding.regulatory_framework.map((fw, i) => (
                          <span
                            key={i}
                            className="text-[11px] px-1.5 py-0.5 rounded bg-accent-tint text-accent-hover font-semibold"
                          >
                            {fw}
                          </span>
                        ))}
                      </dd>
                    </div>
                  )}
                  {finding.applies_to && finding.applies_to.length > 0 && (
                    <LineageItem label="Applies to" value={finding.applies_to.join(', ')} />
                  )}
                  {finding.source_document_celex && (
                    <LineageItem label="CELEX" value={finding.source_document_celex} mono />
                  )}
                  {finding.document_version && (
                    <LineageItem label="Version" value={finding.document_version} mono />
                  )}
                </dl>
              </RailSection>
            )}

            {/* Empty state for the rail if literally nothing is available */}
            {!citationContext?.context &&
              findingMarkers.length === 0 &&
              !fallbackReference &&
              allRefs.length === 0 &&
              (!finding.linked_qa || finding.linked_qa.length === 0) &&
              (!finding.concepts || finding.concepts.length === 0) &&
              !hasLineage && (
                <div className="text-center py-12 text-sm text-fg-tertiary">
                  <Hash size={20} className="mx-auto mb-2 opacity-40" />
                  No related references available yet.
                </div>
              )}
          </aside>
        </div>

        {/* Sticky footer — Open source mirrored here + Esc hint */}
        <footer className="flex-shrink-0 px-5 py-2.5 border-t border-divider bg-bg-warm flex items-center justify-between text-[11px] text-fg-tertiary">
          <div className="flex items-center gap-3">
            {actionLink ? (
              <a
                href={actionLink.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-accent hover:text-accent-hover hover:underline font-medium"
              >
                <Globe size={11} />
                {actionLink.label}
                <ExternalLink size={10} />
              </a>
            ) : (
              <span>No external source link for this finding.</span>
            )}
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <kbd className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-surface border border-border-default text-fg-secondary">
              Esc
            </kbd>
            <span>to close</span>
          </div>
        </footer>
    </DialogShell>
  );
}

/** Small row for the lineage dl: label + value, value optionally mono */
function LineageItem({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-fg-tertiary min-w-[82px] flex-shrink-0">{label}</dt>
      <dd className={`text-fg-secondary min-w-0 break-words ${mono ? 'font-mono text-[12px]' : ''}`}>
        {value}
      </dd>
    </div>
  );
}
